<script setup lang="ts">
import {Component, InputType, State} from "../types.ts";
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import useStore from "../store.ts";

const {item} = defineProps<{
  item: Component
}>();

let selectionBackground = "inherit";
let selectionForeground = "inherit";
let disabledBackground = "inherit";
let disabledForeground = "inherit";
let pressedBackground = "inherit";
let pressedForeground = "inherit";


if (item.button_props) {
  if (item.button_props.selection_colors) {
    selectionBackground = item.button_props.selection_colors.background;
    selectionForeground = item.button_props.selection_colors.foreground
  }

  if (item.button_props.disabled_colors) {
    disabledBackground = item.button_props.disabled_colors.background;
    disabledForeground = item.button_props.disabled_colors.foreground
  }

  if (item.button_props.pressed_colors) {
    pressedBackground = item.button_props.pressed_colors.background;
    pressedForeground = item.button_props.pressed_colors.foreground
  }
}


const stateToPseudo: Partial<Record<State, string>> = {
  [State.ACTIVE]: ":active",
  [State.CHECKED]: ":checked",
  [State.DISABLED]: ":disabled",
  [State.EMPTY]: ":empty",
  [State.ENABLED]: ":enabled",
  [State.FOCUS]: ":focus",
  [State.HOVER]: ":hover",
  [State.INVALID]: ":invalid",
  [State.LINK]: ":link",
  [State.OPTIONAL]: ":optional",
  [State.REQUIRED]: ":required",
  [State.VALID]: ":valid",
  [State.VISITED]: ":visited",
};

const buttonClass = computed(
    () => `data-object-button-${(item.props?.id || "button").replace(/[^a-zA-Z0-9_-]/g, "-")}`
);

const stateStylesCSS = computed(() => {
  const styles = item.button_props?.state_style || [];
  return styles
      .map(({state, css_style}) => {
        const pseudo = stateToPseudo[state];
        if (!pseudo || !css_style) return "";
        return `.${buttonClass.value}${pseudo}{${css_style}}`;
      })
      .filter(Boolean)
      .join("\n")
      .trim();
});

const styleEl = ref<HTMLStyleElement | null>(null);
const type = ref("text");

const ensureStyleEl = () => {
  if (styleEl.value) return styleEl.value;
  const el = document.createElement("style");
  el.dataset.buttonStyle = item.props?.id || "button";
  document.head.appendChild(el);
  styleEl.value = el;
  return el;
};

onMounted(() => {
  if (!stateStylesCSS.value) return;
  const el = ensureStyleEl();
  el.textContent = stateStylesCSS.value;
});

onMounted(() => {
  item.props.style.justifyContent = undefined;
  item.props.style.alignItems = undefined;
  item.style.justifyContent = undefined as unknown as string;
  item.style.alignItems = undefined as unknown as string;


  switch (item.input_text_props.input_type) {
    case InputType.INPUT_TYPE_ALPHANUMERIC: {
      type.value = "text";
      break;
    }
    case InputType.INPUT_TYPE_NUMERIC: {
      type.value = "number"
      break;
    }
  }

  const store = useStore();
  store.socketManager.requestButtonStatus(item);
});



watch(stateStylesCSS, (css) => {
  if (!css) {
    if (styleEl.value?.parentNode) {
      styleEl.value.parentNode.removeChild(styleEl.value);
    }
    styleEl.value = null;
    return;
  }
  const el = ensureStyleEl();
  el.textContent = css;
});

onBeforeUnmount(() => {
  if (styleEl.value?.parentNode) {
    styleEl.value.parentNode.removeChild(styleEl.value);
  }
  styleEl.value = null;
});

let debounceTimeout: NodeJS.Timeout | null = null;

const handleInput = (event: InputEvent) => {
  event.preventDefault();
  const store = useStore();
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }
  debounceTimeout = setTimeout(() => {
    if (event.target == null) return;
    let value = (event?.target as HTMLInputElement).value;
    store.socketManager.sendTextUpdate(item, value ?? null);
  }, 300);
};

</script>
<template>
  <div v-bind="item.props">
    <input :id="`input-${item.props.id}`"
           :style="{position: 'absolute', width: '100%', height: '100%', fontSize: item.props.style[`font-size`]}"
           :type="type"
           :value="item.published.src"
           :maxlength="item.input_text_props.max_length"
           @input="handleInput"
    />
     <slot>
    </slot>

  </div>
</template>

<style scoped>
input:focus {
  background: v-bind(selectionBackground) !important;
  color: v-bind(selectionForeground) !important;
}

input:disabled {
  background: v-bind(disabledBackground) !important;
  color: v-bind(disabledForeground) !important;
}

input:active {
  background: v-bind(pressedBackground) !important;
  color: v-bind(pressedForeground) !important;
}
</style>